import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Users, Package, Crown, TrendingUp, CheckCircle, XCircle, Eye, Plus, Trash2, Edit, FolderTree, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { ModelFormDialog } from '../components/ModelFormDialog';
import { CategoriesManager } from '../components/CategoriesManager';
import { AdminSettings } from '../components/AdminSettings';
import { API } from '../lib/api';

export function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [premiumRequests, setPremiumRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModelForm, setShowModelForm] = useState(false);
  const [editingModel, setEditingModel] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'admin') {
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      const [statsRes, requestsRes, usersRes, modelsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/premium-requests`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/models`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setPremiumRequests(requestsRes.data);
      setUsers(usersRes.data);
      setModels(modelsRes.data);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePremium = async (requestId) => {
    try {
      await axios.post(`${API}/admin/premium-requests/${requestId}/approve`, {}, { withCredentials: true });
      toast.success('Solicitação premium aprovada');
      await loadData();
    } catch (error) {
      toast.error('Erro ao aprovar solicitação');
    }
  };

  const handleRejectPremium = async (requestId) => {
    try {
      await axios.post(`${API}/admin/premium-requests/${requestId}/reject`, {}, { withCredentials: true });
      toast.success('Solicitação premium rejeitada');
      await loadData();
    } catch (error) {
      toast.error('Erro ao rejeitar solicitação');
    }
  };

  const handleDeleteModel = async (modelId) => {
    if (!window.confirm('Tem certeza que deseja deletar este modelo?')) return;
    try {
      await axios.delete(`${API}/models/${modelId}`, { withCredentials: true });
      toast.success('Modelo deletado');
      await loadData();
    } catch (error) {
      toast.error('Erro ao deletar modelo');
    }
  };

  const handleTogglePremium = async (userId, currentPremium) => {
    const action = currentPremium ? 'remover' : 'conceder';
    if (!window.confirm(`Tem certeza que deseja ${action} acesso Premium para este usuário?`)) return;
    try {
      await axios.post(
        `${API}/admin/users/${userId}/set-premium`,
        { is_premium: !currentPremium },
        { withCredentials: true }
      );
      toast.success(currentPremium ? 'Premium removido' : 'Premium concedido');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar Premium');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Tem certeza que deseja deletar o usuário "${userName}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success('Usuário deletado');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao deletar usuário');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-pulse text-[#0066FF] text-xl">Carregando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      <main data-testid="admin-dashboard" className="mt-16 pt-8 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-4xl font-black text-white tracking-tight mb-2">Painel Administrativo</h1>
            <p className="text-zinc-400">Gerencie usuários, modelos e solicitações premium</p>
          </div>

          <div className="flex space-x-2 mb-8 overflow-x-auto pb-2">
            {[
              { id: 'overview', label: 'Visão Geral', icon: TrendingUp },
              { id: 'requests', label: 'Solicitações Premium', icon: Crown },
              { id: 'users', label: 'Usuários', icon: Users },
              { id: 'models', label: 'Modelos', icon: Package },
              { id: 'categories', label: 'Categorias', icon: FolderTree },
              { id: 'settings', label: 'Configurações', icon: Settings }
            ].map((tab) => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-[#0066FF] text-white'
                    : 'bg-[#1A1A1A] text-zinc-400 hover:text-white border border-[#27272A]'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {activeTab === 'overview' && stats && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="overview-section"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Users className="w-8 h-8 text-[#0066FF]" />
                    <span className="text-3xl font-black text-white">{stats.total_users}</span>
                  </div>
                  <p className="text-zinc-400 font-semibold">Total Users</p>
                </div>
                <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Crown className="w-8 h-8 text-[#00FF99]" />
                    <span className="text-3xl font-black text-white">{stats.premium_users}</span>
                  </div>
                  <p className="text-zinc-400 font-semibold">Premium Users</p>
                </div>
                <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Package className="w-8 h-8 text-[#0066FF]" />
                    <span className="text-3xl font-black text-white">{stats.total_models}</span>
                  </div>
                  <p className="text-zinc-400 font-semibold">Total Models</p>
                </div>
                <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <TrendingUp className="w-8 h-8 text-yellow-500" />
                    <span className="text-3xl font-black text-white">{stats.pending_requests}</span>
                  </div>
                  <p className="text-zinc-400 font-semibold">Pending Requests</p>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'requests' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="requests-section"
            >
              <div className="backdrop-blur-xl bg-[#121212]/60 border border-white/10 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#1A1A1A] border-b border-[#27272A]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-bold text-white">User</th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-white">Email</th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-white">Status</th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-white">Receipt</th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-white">Date</th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-white">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#27272A]">
                      {premiumRequests.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-6 py-8 text-center text-zinc-500">
                            No premium requests
                          </td>
                        </tr>
                      ) : (
                        premiumRequests.map((req) => (
                          <tr key={req.id} data-testid={`request-${req.id}`} className="hover:bg-[#1A1A1A]/50">
                            <td className="px-6 py-4 text-white font-semibold">{req.user_name}</td>
                            <td className="px-6 py-4 text-zinc-400">{req.user_email}</td>
                            <td className="px-6 py-4">
                              <span
                                className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                                  req.status === 'pending'
                                    ? 'bg-yellow-500/20 text-yellow-500'
                                    : req.status === 'approved'
                                    ? 'bg-green-500/20 text-green-500'
                                    : 'bg-red-500/20 text-red-500'
                                }`}
                              >
                                {req.status.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              {req.receipt_path ? (
                                <a
                                  href={`${API}/storage/files/${req.receipt_path}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#0066FF] hover:text-[#3385FF] flex items-center space-x-1"
                                >
                                  <Eye className="w-4 h-4" />
                                  <span>Ver</span>
                                </a>
                              ) : (
                                <span className="text-zinc-600">Nenhum</span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-zinc-400 text-sm">
                              {new Date(req.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4">
                              {req.status === 'pending' && (
                                <div className="flex items-center space-x-2">
                                  <button
                                    data-testid={`approve-${req.id}`}
                                    onClick={() => handleApprovePremium(req.id)}
                                    className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                                    title="Aprovar"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </button>
                                  <button
                                    data-testid={`reject-${req.id}`}
                                    onClick={() => handleRejectPremium(req.id)}
                                    className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                                    title="Rejeitar"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'users' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="users-section"
            >
              <div className="backdrop-blur-xl bg-[#121212]/60 border border-white/10 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#1A1A1A] border-b border-[#27272A]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-bold text-white">Nome</th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-white">Email</th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-white">País</th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-white">Função</th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-white">Premium</th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-white">Cadastro</th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-white">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#27272A]">
                      {users.map((u) => {
                        const isSelf = u._id === user?._id;
                        const isAdminUser = u.role === 'admin';
                        return (
                          <tr key={u._id} data-testid={`user-row-${u._id}`} className="hover:bg-[#1A1A1A]/50">
                            <td className="px-6 py-4 text-white font-semibold">{u.name}</td>
                            <td className="px-6 py-4 text-zinc-400">{u.email}</td>
                            <td className="px-6 py-4 text-zinc-400">{u.country}</td>
                            <td className="px-6 py-4">
                              <span
                                className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                                  isAdminUser ? 'bg-[#0066FF]/20 text-[#0066FF]' : 'bg-[#1A1A1A] text-zinc-400'
                                }`}
                              >
                                {u.role}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              {u.is_premium ? (
                                <CheckCircle className="w-5 h-5 text-[#00FF99]" />
                              ) : (
                                <XCircle className="w-5 h-5 text-zinc-600" />
                              )}
                            </td>
                            <td className="px-6 py-4 text-zinc-400 text-sm">
                              {new Date(u.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-2">
                                {!isAdminUser && (
                                  <button
                                    data-testid={`toggle-premium-${u._id}`}
                                    onClick={() => handleTogglePremium(u._id, u.is_premium)}
                                    title={u.is_premium ? 'Remover Premium' : 'Conceder Premium'}
                                    className={`p-2 rounded-lg border transition-colors ${
                                      u.is_premium
                                        ? 'bg-red-500/20 border-red-500/50 text-red-500 hover:bg-red-500/30'
                                        : 'bg-[#00FF99]/20 border-[#00FF99]/50 text-[#00FF99] hover:bg-[#00FF99]/30'
                                    }`}
                                  >
                                    <Crown className="w-4 h-4" />
                                  </button>
                                )}
                                {!isSelf && !isAdminUser && (
                                  <button
                                    data-testid={`delete-user-${u._id}`}
                                    onClick={() => handleDeleteUser(u._id, u.name)}
                                    title="Deletar usuário"
                                    className="p-2 bg-red-500/20 border border-red-500/50 text-red-500 rounded-lg hover:bg-red-500/30 transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                                {(isSelf || isAdminUser) && (
                                  <span className="text-xs text-zinc-600">—</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'models' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="models-section"
            >
              <div className="mb-6">
                <button
                  data-testid="add-model-button"
                  onClick={() => { setEditingModel(null); setShowModelForm(true); }}
                  className="flex items-center space-x-2 bg-[#0066FF] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#3385FF] transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  <span>Adicionar Novo Modelo</span>
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {models.map((model) => (
                  <div key={model.id} className="bg-[#121212] border border-[#27272A] rounded-lg overflow-hidden group">
                    <div className="aspect-video bg-[#1A1A1A]">
                      {model.images && model.images[0] ? (
                        <img src={model.images[0]} alt={model.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-zinc-600">Sem imagem</div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="text-white font-bold mb-2 line-clamp-1">{model.title}</h3>
                      <div className="flex items-center space-x-2 mb-3">
                        {model.is_featured && (
                          <span className="bg-[#0066FF]/20 text-[#0066FF] text-xs font-bold px-2 py-1 rounded">DESTAQUE</span>
                        )}
                        {model.is_premium && (
                          <span className="bg-[#00FF99]/20 text-[#00FF99] text-xs font-bold px-2 py-1 rounded">PREMIUM</span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => navigate(`/model/${model.id}`)}
                          className="flex-1 bg-[#1A1A1A] border border-[#27272A] text-white px-4 py-2 rounded hover:border-[#0066FF] transition-colors text-sm font-semibold"
                        >
                          Ver
                        </button>
                        <button
                          data-testid={`edit-model-${model.id}`}
                          onClick={() => { setEditingModel(model); setShowModelForm(true); }}
                          className="p-2 bg-[#0066FF]/20 border border-[#0066FF]/50 text-[#0066FF] rounded hover:bg-[#0066FF]/30 transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`delete-model-${model.id}`}
                          onClick={() => handleDeleteModel(model.id)}
                          className="p-2 bg-red-500/20 border border-red-500/50 text-red-500 rounded hover:bg-red-500/30 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
          {activeTab === 'categories' && <CategoriesManager />}

          {activeTab === 'settings' && <AdminSettings />}
        </div>
      </main>

      <ModelFormDialog
        open={showModelForm}
        onClose={() => { setShowModelForm(false); setEditingModel(null); }}
        onSuccess={loadData}
        model={editingModel}
      />
    </div>
  );
}